import { DEFAULT_CONFIG } from "./defaults";
import type { SignalingSpec } from "./types";
import { validateLayer } from "./validate";

export interface RoomLink {
  /** Room secret (validated by @codeshare/crypto when deriving keys). */
  secret: string;
  /** Signaling the room uses, when it isn't the default. Peers must share a medium. */
  signaling?: SignalingSpec[];
}

const SECRET_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

function encodeJson(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeJson(text: string): unknown {
  const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  return JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))));
}

const sameSignaling = (a: SignalingSpec[], b: SignalingSpec[]) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * The shareable link. Everything rides in the fragment, which browsers never send to the server.
 * TURN credentials are never included.
 */
export function buildRoomLink(base: string, link: RoomLink): string {
  const url = new URL(base);
  const params = new URLSearchParams({ r: link.secret });
  if (link.signaling && !sameSignaling(link.signaling, DEFAULT_CONFIG.signaling)) {
    params.set("s", encodeJson(link.signaling));
  }
  url.hash = params.toString();
  return url.toString();
}

/** Reads a room link (or just its fragment). Undefined if there's no well-formed room secret. */
export function parseRoomLink(
  urlOrFragment: string,
  warn: (message: string) => void = () => {},
): RoomLink | undefined {
  const hashIndex = urlOrFragment.indexOf("#");
  const fragment = hashIndex >= 0 ? urlOrFragment.slice(hashIndex + 1) : urlOrFragment;
  const params = new URLSearchParams(fragment);
  const secret = params.get("r");
  if (!secret || !SECRET_PATTERN.test(secret)) return undefined;

  const encoded = params.get("s");
  if (!encoded) return { secret };
  let signaling: SignalingSpec[] | undefined;
  try {
    signaling = validateLayer({ signaling: decodeJson(encoded) }, warn).signaling;
  } catch {
    warn("ignoring unreadable signaling settings in link");
  }
  return signaling ? { secret, signaling } : { secret };
}
